import { useEffect, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import swal from "sweetalert";
import { aiService } from "../../service/api.service";
import { useLocation, useNavigate } from "react-router-dom";
import ChangeLimitModal from "./ChangeLimitModal";
import CustomerAccountsToolbar from "./CustomerAccountsToolbar";
import CustomerAccountsTable from "./CustomerAccountsTable";

const SUBSCRIPTION_TYPES = ["Trial", "Standard", "Premium", "Enterprise"];
const SUBSCRIPTION_STATUSES = ["Active", "Expired", "Cancelled"];

function UserList() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryStatus = new URLSearchParams(location.search).get("status");
  const initialStatusFilter = ["active", "inactive"].includes(queryStatus)
    ? queryStatus
    : "";
  const querySubscription = new URLSearchParams(location.search).get(
    "subscription",
  );
  const initialSubscriptionFilter = SUBSCRIPTION_TYPES.includes(
    querySubscription,
  )
    ? querySubscription
    : "";
  const querySubscriptionStatus = new URLSearchParams(location.search).get(
    "subscriptionStatus",
  );
  const initialSubscriptionStatusFilter = SUBSCRIPTION_STATUSES.includes(
    querySubscriptionStatus,
  )
    ? querySubscriptionStatus
    : "";
  const [listLoading, setListLoading] = useState(false);
  const [rowActionLoading, setRowActionLoading] = useState({});
  const [limitModal, setLimitModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [subscriptionFilter, setSubscriptionFilter] = useState(
    initialSubscriptionFilter,
  );
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState(
    initialSubscriptionStatusFilter,
  );
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(1);
  const [page, setPage] = useState(1);
  const [debouncedQuery, setDebouncedQuery] = useState(search);
  const page_size = 10;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(search);
      setPage(1);
    }, 300);
    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    getUserList();
  }, [page, statusFilter, subscriptionFilter, subscriptionStatusFilter, debouncedQuery]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    setStatusFilter(["active", "inactive"].includes(status) ? status : "");
    const subscription = params.get("subscription");
    setSubscriptionFilter(
      SUBSCRIPTION_TYPES.includes(subscription) ? subscription : "",
    );
    const subscriptionStatus = params.get("subscriptionStatus");
    setSubscriptionStatusFilter(
      SUBSCRIPTION_STATUSES.includes(subscriptionStatus) ? subscriptionStatus : "",
    );
    setPage(1);
  }, [location.search]);

  function handlePageChange(pageNumber) {
    setPage(pageNumber);
  }

  function handleStatusFilterChange(value) {
    setStatusFilter(value);
    setPage(1);
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set("status", value);
    } else {
      params.delete("status");
    }
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true },
    );
  }

  function handleSubscriptionFilterChange(value) {
    setSubscriptionFilter(value);
    setPage(1);
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set("subscription", value);
    } else {
      params.delete("subscription");
    }
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true },
    );
  }

  function handleSubscriptionStatusFilterChange(value) {
    setSubscriptionStatusFilter(value);
    setPage(1);
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set("subscriptionStatus", value);
    } else {
      params.delete("subscriptionStatus");
    }
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true },
    );
  }

  async function getUserList() {
    setListLoading(true);
    try {
      const users = await aiService.userList({
        search: debouncedQuery,
        limit: page_size,
        page,
        status: statusFilter,
        subscription: subscriptionFilter,
        subscriptionStatus: subscriptionStatusFilter,
      });
      const userRows = users.data.data || [];
      const usersWithDetails = userRows.map((user) => ({
        ...user,
        currentSubscription: user.currentSubscription || null,
      }));

      setData(usersWithDetails);
      setTotalItems(users.data.total_count);
    } catch (error) {
      if (error?.response?.status == 401) {
        swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(() => {
          localStorage.clear();
          window.location.href = "/";
        });
      }
    } finally {
      setListLoading(false);
    }
  }

  async function changeStatus(id, is_active) {
    swal({
      title: "Confirmation",
      text: `Are you sure you want to ${is_active ? "deactivate" : "activate"} the User?`,
      icon: "warning",
      buttons: ["NO", "YES"],
    }).then(async (val) => {
      if (val) {
        setRowActionLoading((prev) => ({ ...prev, [id]: true }));
        try {
          await aiService.ChangeStatus(id, {
            is_active: is_active ? false : true,
          });
          swal({
            icon: "success",
            text: `User ${is_active ? "deactivated" : "activated"} successfully.`,
          });
          await getUserList();
        } catch (error) {
          if (error?.response?.status == 401) {
            swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(
              () => {
                localStorage.clear();
                window.location.href = "/";
              },
            );
          }
          return false;
        } finally {
          setRowActionLoading((prev) => ({ ...prev, [id]: false }));
        }
      }
    });
  }

  async function deleteUser(id) {
    swal({
      title: "Confirmation",
      text: `Are you sure you want to delete the User?`,
      icon: "warning",
      buttons: ["NO", "YES"],
    }).then(async (val) => {
      if (val) {
        setRowActionLoading((prev) => ({ ...prev, [id]: true }));
        try {
          await aiService.deleteUser(id);
          swal({ icon: "success", text: "User deleted successfully." });
          await getUserList();
        } catch (error) {
          if (error?.response?.status == 401) {
            swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(
              () => {
                localStorage.clear();
                window.location.href = "/";
              },
            );
          }
        } finally {
          setRowActionLoading((prev) => ({ ...prev, [id]: false }));
        }
      }
    });
  }

  const openLimitModal = (user) => {
    setSelectedUser(user);
    setLimitModal(true);
  };

  const handleLimitModalClose = () => {
    setLimitModal(false);
    setSelectedUser(null);
  };

  return (
    <div className="dashboard-main-area company-list-area">
      <Container fluid>
        <div className="dashboard-area-heading management-heading">
          <div className="under-content-section">
            <div>
              <h1 className="company-page-title">Company Accounts</h1>
              <h5 className="company-page-subtitle">
                {data.length} of {totalItems} company accounts
              </h5>
            </div>
            <div className="header-actions-right">
              <Button
                variant="primary"
                className="agent-primary-btn"
                onClick={() => navigate("/create-company")}
              >
                Create new company
              </Button>
            </div>
          </div>
          <Row className="pb-4 justify-content-center">
            <Col md={12}>
              <CustomerAccountsToolbar
                search={search}
                onSearchChange={setSearch}
                status={statusFilter}
                onStatusChange={handleStatusFilterChange}
                subscription={subscriptionFilter}
                onSubscriptionChange={handleSubscriptionFilterChange}
                subscriptionStatus={subscriptionStatusFilter}
                onSubscriptionStatusChange={handleSubscriptionStatusFilterChange}
              />
              <div className="audit-request-box premium-company-card">
                <CustomerAccountsTable
                  data={data}
                  listLoading={listLoading}
                  pageSize={page_size}
                  rowActionLoading={rowActionLoading}
                  onChangeStatus={changeStatus}
                  onDeleteUser={deleteUser}
                  onOpenLimitModal={openLimitModal}
                  onNavigateToAgents={(u) =>
                    navigate(
                      `/agent-list?user_id=${u._id}&user_name=${encodeURIComponent(u.name || "")}`,
                      { state: { userId: u._id, userName: u.name } },
                    )
                  }
                  onOpenUserInfo={(u) =>
                    navigate(
                      `/company-info?user_id=${u._id}&user_name=${encodeURIComponent(u.name || "")}&user_email=${encodeURIComponent(u.email || "")}`,
                      {
                        state: {
                          userId: u._id,
                          userName: u.name,
                          userEmail: u.email,
                          user: u,
                          fromUserList: true,
                        },
                      },
                    )
                  }
                  page={page}
                  totalItems={totalItems}
                  onPageChange={handlePageChange}
                />
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      <ChangeLimitModal
        show={limitModal}
        handleClose={handleLimitModalClose}
        user={selectedUser}
        onSuccess={getUserList}
      />
    </div>
  );
}

export default UserList;
